import i18n, {DefaultLanguage} from '@/plugins/i18n'
import API from '@/plugins/api.js'
import Rightsholder from "@/models/Rightsholder";
import Statement from "@/models/Statement";
import Right from "@/models/Right";
import NotificationChain from "@/models/NotificationChain";
import StagedRight from "@/models/StagedRight";
import Claimform from "@/models/Claimform";
import loGet from 'lodash/get'

const actions = {
  setLoading({commit}, value) {
    commit('SET_LOADING', value)
  },

  setInitialized({commit}, value) {
    commit('SET_INITIALIZED', value)
  },

  switchLocale({dispatch},value) {
    dispatch('setLocale', value)
  },

  setLocale({commit}, value) {
    commit('SET_LOCALE', value)
    localStorage.setItem('locale', value)
    i18n.locale = value || DefaultLanguage
    // RFC 7231, section 5.3.5
    API.setHeaders('Accept-Language', value)
    try {
      API.actions.Settings.updateLang(value);
      // eslint-disable-next-line no-empty
    } catch (e) {
    }
  },

  loadLocale({dispatch}) {
    return API.ready(() => {
      const locale = localStorage.getItem('locale')
      i18n.locale = locale || DefaultLanguage
      return dispatch('setLocale', locale || DefaultLanguage)
    });
  },
  //        ctx.dispatch('loadHomeContent', ctx.getters.locale)
  loadHomeContent(ctx, lang) {
    return API.ready(() =>
      API.actions.Content.getHomePage(lang)
        .then((res) => {
          ctx.commit('SET_HOME_CONTENT', res.data);
        })
    );
  },

  loadStartContent(ctx,lang) {
    return API.ready(() => 
      API.actions.Content.getStartPage(lang)
        .then((res) => {
          ctx.commit('SET_START_CONTENT', res.data);
        })
    );
  },

  loadLoginContent(ctx,lang) {
    console.log('[store] loadLoginContent', lang)
    return API.ready(() =>
      API.actions.Content.getLoginPage(lang)
        .then((res) => {
          ctx.commit('SET_LOGIN_CONTENT', res.data);
        })
        .catch(() => undefined)
    );
  },

  loadDocumentContent(ctx,lang) {
    return API.ready(() => 
      API.actions.Content.getDocumentPage(lang)
        .then((res) => {
          ctx.commit('SET_DOCUMENT_CONTENT', res.data);
        })
    );
  },


  setAuthenticated(ctx) {
    console.log('[store] setAuthenticated')
    ctx.commit('SET_AUTH', true);
  },

  setUserData(ctx, auth0data) {
    console.log('[store] setUserData – calling getCurrentUserInfo, isReady:', API.isReady, 'endpointList keys:', Object.keys(API.endpointList).length)
    API.actions.User.getCurrentUserInfo()
      .then((res) => {
        const userData = res.data.rows[0];
        //- For admin we can lazy load stuff, for others loadRightsholders must be loaded first
        if (userData && userData.isadmin) {
          ctx.commit('SET_USER_DATA', {user: {...auth0data, ...userData}, rightsholders: []})
          ctx.dispatch('loadCurrentRightsholderData')
          ctx.dispatch('loadRightsholders')
        } else {
          ctx.dispatch('loadRightsholders')
            .then((res) => ctx.commit('SET_USER_DATA', {user: {...auth0data, ...userData}, rightsholders: res}))
            .then(() => ctx.dispatch('loadCurrentRightsholderData'))
        }
      })
      .then(() => API.actions.Settings.fetch())
      .then(res => {
        const settings = res.data.rows[0];
        ctx.commit('SET_USER_SETTINGS', settings);
      })
  },

  submitCustomForm(ctx, {data, method, url } = {}) {
    return API.anywhere(method, url, data, ctx.state.currentRightsholderId)
      .then((res) => {
        return res
      })
  },

  setNotificationsPollingInterval(ctx, interval) {
    ctx.commit('SET_NOTIFICATIONS_POLLING_INTERVAL', interval);
  },

  stopNotificationsPoll(ctx) {
    const interval = ctx.getters.notificationsPollingInterval;
    if (interval) {
      window.clearTimeout(this.notificationsPollingInterval);
      ctx.dispatch('setNotificationsPollingInterval', null);
    }
  },

  loadNotifications(ctx, {force = false} = {}) {
    if (!ctx.state.currentRightsholderId) {
      return Promise.reject(new Error('No current rightsholder is set!'));
    }
    if (force) {
      ctx.commit('CLEAR_NOTIFICATIONS');
    }

    const bol = ctx.getters.isAdmin ? ctx.getters.adminId : ctx.state.currentRightsholderId;
    return API.actions.Messages.getList(bol, ctx.state.notifications.pagesLoaded + 1)
      .then((res) => {
        const list = (res.data.rows || []).map((d) => new NotificationChain(d));

        ctx.commit('APPEND_NOTIFICATIONS', list);

      })
  },

  pollLoadNotifications(ctx) {
    if (!ctx.state.currentRightsholderId) {
      return Promise.reject(new Error('No current rightsholder is set!'));
    }
    const bol = ctx.getters.isAdmin ? ctx.getters.adminId : ctx.state.currentRightsholderId;
    return API.actions.Messages.getList(bol, 1)
      .then((res) => {
        const list = (res.data.rows || []).map((d) => new NotificationChain(d));
        const unreadCount = (list ? list.reduce((count, {isUnread, fromEmail}) => (fromEmail === ctx.getters.user.email ? count : count + Number(isUnread)), 0) : 0);
        const result = {result: unreadCount - ctx.getters.unreadNotificationsCount};
        if (unreadCount !== ctx.getters.unreadNotificationsCount) {
          // launch mutation to reset data
          ctx.commit('UPDATE_POLLED_NOTIFICATIONS', list);

        }
        return result;
      });
  },

  markMessageRead(ctx, mId) {
    const bol = ctx.getters.isAdmin ? ctx.getters.adminId : ctx.state.currentRightsholderId;
    return API.actions.Messages.markRead(mId, bol)
      .then(() => {
        ctx.commit('MARK_NOTIFICATION_READ', mId);
        return {};
      });
  },

  sendNotificationFromAdmin(ctx, {to, text, title}) {
    const rows = Array.isArray(to) ? to.map(tbol => ({tbol})) : [];

    return API.actions.Messages.sendMessage({
      rows,
      text,
      title,
      ...(to === 0 ? {tbol: 0} : {}),
      bol: ctx.getters.adminId
    }).then(() => {
      ctx.dispatch('loadNotifications', {force: true})
    });
  },

  sendNotificationToAdmin(ctx, {text, title = `Message from ${ctx.getters.user.name}`}) {

    return API.actions.Messages.sendMessage({
      text,
      title,
      tbol: ctx.getters.adminId,
      bol: ctx.state.currentRightsholderId
    }).then(() => {
      ctx.dispatch('loadNotifications', {force: true})
    })
  },

  sendNotificationReply(ctx, {mid, text, title}) {
    const bol = ctx.getters.isAdmin ? ctx.getters.adminId : ctx.state.currentRightsholderId;
    return API.actions.Messages.sendReply(mid, {
      title,
      text,
      bol,
    }).then(() => {
      ctx.dispatch('loadNotifications', {force: true})
    });
  },

  loadDocuments(ctx) {
    return API.ready(() => {
      if (ctx.getters.isAuthenticated) {
        return API.ready(() => API.actions.Documents.Private.getList()
          .then((res) => res.data))
      }
      return API.actions.Documents.Public.getList()
        .then((res) => res.data)

    });
  },

  downloadDocument(ctx, {uri, type}) {
    return API.ready(() =>
      API.actions.Documents[ctx.getters.isAuthenticated ? 'Private' : 'Public'].getFile(uri)
        .then((res) => {
          const doctype = type || res.headers['content-type'];
          const blob = new Blob([res.data], {type: doctype});
          return window.URL.createObjectURL(blob);
        })
    )
  },

  downloadPrivateDocument(ctx, {uri, type}) {
    return API.ready(() =>
      API.actions.Documents.Private.getFile(uri)
        .then((res) => {
          const doctype = type || res.headers['content-type'];
          const blob = new Blob([res.data], {type: doctype});
          return window.URL.createObjectURL(blob);
        })
    )
  },

  getXLSXTemplateUrl(ctx) {
    return API.actions.Documents.Private.getXLSXTemplate(ctx.state.currentRightsholderId)
      .then(({data}) => data.rows[0]);
  },

  loadRightsholders(ctx) {
    return API.actions.Rightsholders.getList(ctx.state.rightsholders.pagesLoaded + 1)
      .then((res) => {
        if (res.data && res.data.rows) {
          const rhList = res.data.rows.map(row => new Rightsholder(row));
          ctx.commit('APPEND_RIGHTSHOLDERS', rhList);
          return res.data.rows
        }
      });
  },

  loadCurrentRightsholderData(ctx) {
    if (ctx.state.currentRightsholderId) {
      return API.actions.Rightsholders.getDetails(ctx.state.currentRightsholderId)
        .then((res) => {
          const rh = new Rightsholder(res.data.rows[0]);
          ctx.commit('SET_CURRENT_RH_DATA', rh);

          const docuri = loGet(res, 'data.rows[0].docuri', null)
          if (docuri) {
            ctx.dispatch('showRightsholderForm', docuri)
          } else {
            ctx.commit('SET_DIALOG_CONTENT', null)
          }
        })
    }
  },

  resetCurrentRightsholder(ctx, id) {
    ctx.commit('SET_CURRENT_RH', id);
    API.actions.Settings.updateRightsholder(id)
      .then((res) => {
        const docuri = loGet(res, 'data.rows[0].docuri', null)
        if (docuri) {
          ctx.dispatch('showRightsholderForm', docuri)
        } else {
          ctx.commit('SET_DIALOG_CONTENT', null)
        }
      });

    const rhData = ctx.getters.rightsholders.list.find((rh) => rh.id === id);
    if (rhData) {
      ctx.commit('SET_CURRENT_RH_DATA', rhData);
    } else {
      ctx.dispatch('loadCurrentRightsholderData');
    }
  },

  showRightsholderForm(ctx, docuri) {
    API.actions.Settings.getRightsholderForm(ctx.state.currentRightsholderId, docuri)
      .then((res) => {
        ctx.commit('SET_DIALOG_CONTENT', res.data)
      })
  },

  loadStatements(ctx, {reload} = {}) {
    if (reload) {
      ctx.commit('CLEAR_STATEMENTS');
    }
    API.actions.Statements.getList(ctx.state.currentRightsholderId, ctx.state.statements.pagesLoaded + 1)
      .then((res) => {
        if (!res.data.rows) {
          return ctx.commit('APPEND_STATEMENTS', []);
        }
        const list = res.data.rows.map((x) => new Statement(x));
        ctx.commit('APPEND_STATEMENTS', list);
      });
  },

  downloadStatementDetails(ctx, sfnr) {
    return API.actions.Statements.getDetailsBlob(sfnr, ctx.state.currentRightsholderId)
      .then((res) => {
        const type = res.headers['content-type'];
        const blob = new Blob([res.data], {type});
        return window.URL.createObjectURL(blob);
      })
  },

  downloadStatementPlaylist(ctx, sfnr) {
    return API.actions.Statements.getPlaylistBlob(sfnr, ctx.state.currentRightsholderId)
      .then((res) => {
        const type = res.headers['content-type'];
        const blob = new Blob([res.data], {type});
        return window.URL.createObjectURL(blob);
      });
  },

  loadRightsList(ctx, {reload, search, action} = {}) {
    if (reload) {
      ctx.commit('CLEAR_RIGHTS');
    }
    const params = {};
    if (search && search.trim().length > 2) {
      params.search = search;
    }
    if (action == "rightaction") {
      params.rightaction = '1';
    }
    if (action == "DC") {
      if ((params.search && !params.search.match(/s:dc/i)) || !params.search) {
        params.search = "s:dc" + (params.search ? ` ${params.search}` : '')
      }
    }

    return API.actions.Rights.getList(ctx.state.currentRightsholderId, ctx.state.rights.pagesLoaded + 1, params)
      .then((res) => {
        if (!res.data.rows) {
          return ctx.commit('APPEND_RIGHTS_LIST', {list: []});
        }

        const list = res.data.rows.map((d) => new Right(d));
        ctx.commit('APPEND_RIGHTS_LIST', {list, totalCount: res.data.count});
      })
  },

  revokeRights(ctx, {items = []} = {}) {
    const payload = {
      rows: items,
      revoke: true,
    }
    return API.actions.Staging.add(ctx.state.currentRightsholderId, payload);
  },

  getClaimformDetailsDownloadUrl(ctx, params) {
    const clnr = params.clnr
    delete params.clnr
    return API.actions.Claimforms.getDetailsBlob(ctx.state.currentRightsholderId, clnr, params)
      .then((res) => {
        console.log(res)
        const type = res.headers['content-type'];
        const filename = res.headers['content-disposition'].match(/filename="(.+)"/)[1];
        const blob = new Blob([res.data], {type});
        return {url: window.URL.createObjectURL(blob), filename};
      });
  },

  getISRCForSpotifyLink(ctx, uri) {
    return API.actions.Staging.getISRCForSpotifyLink(uri)
      .then((res) => {
        return loGet(res, 'data.rows[0]', null)
      });
  },

  getStagingDownloadUrl(ctx, params) {
    return API.actions.Staging.getListBlob(ctx.state.currentRightsholderId, params)
      .then((res) => {
        const type = res.headers['content-type'];
        const filename = res.headers['content-disposition'].match(/filename="(.+)"/)[1];
        const blob = new Blob([res.data], {type});
        return {url: window.URL.createObjectURL(blob), filename};
      });
  },

  getRightsDownloadUrl(ctx, { search, action, format } = { format: 'xlxs' }) {
    const params = {
      format
    };
    if (search && search.trim().length > 2) {
      params.search = search;
    }
    if (action == "rightaction") {
      params.rightaction = '1';
    }
    if (action == "DC") {
      if ((params.search && !params.search.match(/s:dc/i)) || !params.search) {
        params.search = "s:dc" + (params.search ? ` ${params.search}` : '')
      }
    }

    return API.actions.Rights.getListBlob(ctx.state.currentRightsholderId, params)
      .then((res) => {
        const type = res.headers['content-type'];
        const filename = res.headers['content-disposition'].match(/filename="(.+)"/)[1];
        const blob = new Blob([res.data], {type});
        return {url: window.URL.createObjectURL(blob), filename};
      });
  },

  async addRowsToStaging(ctx, {list}) {
    const currentRH = ctx.state.currentRightsholderId;
    // Step 0. For all rights that have zero isrc, add and retrieve isrc

    const zerosList = list.filter(({isrc}) => !isrc);
    if (zerosList.length) {
      const stagingZeroList = zerosList.map(({spnr, ityp}) => ({spnr, ityp}))
      const zerosResult = (await API.actions.Staging.add(currentRH, {
        rows: stagingZeroList,
        allow_isrc_edit: 1
      })).data.rows;

      for (let row of zerosResult) {
        const listItem = list.find(({spnr, ityp}) => (row.spnr === spnr || row.isrc === spnr) && row.ityp === ityp);
        if (listItem) {
          listItem.isrc = row.isrc;
        }
      }
    }

    // Step 1. Add all rights to staging.
    const stagingList = list.map(({spnr, type: ityp, isrc}) => ({spnr, ityp, isrc}))
    return API.actions.Staging.add(currentRH, {rows: stagingList, allow_isrc_edit: 1});
  },

  editRightsList(ctx, {items: list, progressObservable}) {
    // Step 2. Edit in staging
    return ctx.dispatch('editStagingRights', {items: list, progressObservable});
  },

  loadUnclaimedRightsList(ctx, {reload, search} = {}) {
    if (reload) {
      ctx.commit('CLEAR_UNCLAIMED_RIGHTS_LIST', null);
    }
    const params = {};
    if (search && search.trim().length > 2) {
      params.search = search;
    }
    return API.actions.Unclaimed.getList(ctx.state.currentRightsholderId, ctx.state.unclaimed.pagesLoaded + 1, {...params})
      .then((res) => {
        if (!res.data.rows) {
          return ctx.commit('APPEND_UNCLAIMED_RIGHTS_LIST', {list: []});
        }
        const list = res.data.rows.map((d) => new Right(d));
        ctx.commit('APPEND_UNCLAIMED_RIGHTS_LIST', {list, totalCount: res.data.count});
      })
  },

  getUnclaimedRightsDownloadUrl(ctx, params) {
    return API.actions.Unclaimed.getListBlob(ctx.state.currentRightsholderId, params)
      .then((res) => {
        const type = res.headers['content-type'];
        const filename = res.headers['content-disposition'].match(/filename="(.+)"/)[1];
        const blob = new Blob([res.data], {type});
        return {url: window.URL.createObjectURL(blob), filename};
      });
  },

  loadRightsByISRC(ctx, {isrc}) {
    return API.actions.Rights.getDetails(isrc, ctx.state.currentRightsholderId)
      .then((res) => {
        return res.data.rows || [];
      });
  },

  async addRightsToStaging(ctx, rights) {
    const staged = (await API.actions.Staging.add(
      ctx.state.currentRightsholderId,
      {
        rows: rights.map(({isrc, ityp}) => ({isrc, ityp}))
      }
    )).data.rows
    return staged;
  },

  async createRight(ctx, right) {
    // Step 1. Add to staging
    right.bol = ctx.state.currentRightsholderId;
    if (Array.isArray(right)) {
      try {
        const res = await API.actions.Staging.add(
          ctx.state.currentRightsholderId,
          {
            rows: right.map(r => {return {uri: r}})
          }
        );
        return res
      } catch (err) {
        return err
      }
    } else {
      (await API.actions.Staging.add(
        ctx.state.currentRightsholderId,
        {
          isrc: right.isrc,
          ityp: right.ityp
        }
      ));

      // Step 2. Edit staged row
      return ctx.dispatch('editStagingRights', {items: [right]})
    }
  },

  loadStagingRows(ctx, {reload, search} = {}) {
    if (reload) {
      ctx.commit('CLEAR_STAGING_ROWS');
    }
    const params = {};
    if (search) {
      params.search = search;
    }
    return API.actions.Staging.getList(ctx.state.currentRightsholderId, ctx.state.staging.pagesLoaded + 1, {...params})
      .then((res) => {
        if (!res.data.rows) {
          return ctx.commit('APPEND_STAGING_ROWS', {list: []});
        }
        const list = res.data.rows.map((data) => new StagedRight(data));
        ctx.commit('APPEND_STAGING_ROWS', {list, totalCount: res.count});
        return res.data.meta;
      })
  },

  removeSingleStagingRow(ctx, row) {
    const {ityp, isrc} = row;
    return API.actions.Staging.deleteRow({
      bol: ctx.state.currentRightsholderId,
      isrc,
      ityp
    })
      .then(() => {
        ctx.commit('REMOVE_STAGING_ROW', row)
      })
  },

  async editStagingRights(ctx, {items: list, progressObservable = {value: 0}}) {
    const currentRH = ctx.state.currentRightsholderId;

    // Step 2. Edit all rows in staging.
    let result;
    let idx = 0;
    try {
      // eslint-disable-next-line no-await-in-loop
      result = await API.actions.Staging.updateRow({
        bol: currentRH,
        rows: list.map(row => { 
          return {
            ityp: row.type,
            isrc: row.isrc,
            ...row
          }
        })
      });
    } catch (e) {
      console.error(e);
    } finally {
      progressObservable.value = (idx + 1) / list.length;
      idx++;
    }
    return result;
  },

  loadStagingRowErrors({state}, {ityp, isrc}) {
    return API.actions.Staging.getRow({ityp, isrc, bol: state.currentRightsholderId})
      .then((res) => {
        return res.data.rows[0];
      })
  },

  removeStagingRows(ctx, list) {
    return API.actions.Staging.deleteRows({
      bol: ctx.state.currentRightsholderId,
      rows: list.map(i => {return {isrc: i.isrc, ityp: i.ityp}})
    })
      .then(res => {
        console.log(res)
        for (const row of list) {
          ctx.commit('REMOVE_STAGING_ROW', row)
        }
      })
  },

  uploadFileToStaging(ctx, {files, revoke, commit}) {
    return API.actions.Staging.uploadFile(ctx.state.currentRightsholderId, files, revoke, commit);
  },

  getStagingFiles(ctx) {
    return API.actions.Staging.getFiles(ctx.state.currentRightsholderId);
  },

  getStagingFile(ctx, {nb1id}) {
    return API.actions.Staging.getFile(ctx.state.currentRightsholderId, nb1id);
  },

  getStagingFileDownloadUrl(ctx, {nb1id, format}) {
    const params = {
      format
    };
    return API.actions.Staging.getFileBlob(ctx.state.currentRightsholderId, nb1id, params)
      .then((res) => {
        console.log(res)
        const type = res.headers['content-type'];
        const filename = res.headers['content-disposition'].match(/filename="(.+)"/)[1];
        const blob = new Blob([res.data], {type});
        return {url: window.URL.createObjectURL(blob), filename};
      });
  },


  async createClaimform(ctx, {title, items, progressObservable = {}}) {
    const currentRH = ctx.state.currentRightsholderId;
    // Step 1. Mark rows as commited
    progressObservable.value = 1 / 2;
    await API.actions.Staging.updateRow({
      bol: currentRH,
      commit: 1,
      rows: items.map(row => { 
        return {
          isrc: row.isrc,
          ityp: row.ityp,
        }
      })
    });


    progressObservable.value = 2 / 2;

    // Step 2. Create an empty claimform
    const res = (await API.actions.Claimforms.create(currentRH, {
      description: title
    })).data.rows;

    progressObservable.value = -1;

    // Step 3. Add rows to claimform
    return API.actions.Claimforms.addRows(currentRH, res[0].clnr, {
      fromstaging: 'all',
    })
  },

  loadClaimforms(ctx, {reload}) {
    if (reload) {
      ctx.commit('CLEAR_CLAIMFORMS');
    }
    return API.actions.Claimforms.getList(ctx.state.currentRightsholderId)
      .then((res) => {
        if (!res.data.rows) {
          return ctx.commit('APPEND_CLAIMFORMS', {list: []})
        }
        const list = res.data.rows.map((x) => new Claimform(x));
        ctx.commit('APPEND_CLAIMFORMS', {list, totalCount: res.count});
      })
  },

  loadClaimformsRows(ctx, {id}) {
    return API.actions.Claimforms.getRows(ctx.state.currentRightsholderId, id)
      .then((res) => {
        return (res.data.rows || []).map((data) => new Right(data));
      });
  },

  cancelClaimform(ctx, {item: claimform, remove = false}) {
    return API.actions.Claimforms.cancel(ctx.state.currentRightsholderId, {clnr: claimform.id, remove})
      .finally(() => {
        ctx.commit('REMOVE_CLAIMFORM', claimform)
      })
  },

  removeClaimformRow(ctx, row) {
    return API.actions.Claimforms.deleteRow(
      ctx.state.currentRightsholderId,
      row.clnr,
      row.clrnr
    )
  },

  signForm(ctx, {item, code}) {
    return API.actions.Claimforms.updateStatus(ctx.state.currentRightsholderId, {
      clnr: item.clnr,
      status: 'usersign',
      code,
    })
      .then(({data}) => data);
  }
}

export default actions
